import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, throwError } from 'rxjs';
import { UpdateRecommededRouteTechModePayload } from '../interfaces';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { SessionStorageService } from 'src/app/services';
import { TechItemsRouteService } from 'src/app/pages/custom-route/services/tech-items-route.service';
import { InformerRouteRegisterService } from 'src/app/pages/register-user/services/informer-route-register.service';

@Injectable({
  providedIn: 'root',
})
export class UpdateRecommendedRouteTechMode {
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _sessionStorageService = inject(SessionStorageService);
  private _techItemsRoute = inject(TechItemsRouteService);
  private _informerRouteRegister = inject(InformerRouteRegisterService);
  
  private _isLoadingUpdateRecommendedRouteTechMode = signal<boolean>(false);
  public isLoadingUpdateRecommendedRouteTechMode = this._isLoadingUpdateRecommendedRouteTechMode.asReadonly();
  
  private _successMessageUpdateRecommendedRouteTechMode = signal<string>('');
  public successMessageUpdateRecommendedRouteTechMode = this._successMessageUpdateRecommendedRouteTechMode.asReadonly();
  
  private _errorMessageUpdateRecommendedRouteTechMode = signal<string>('');
  public errorMessageUpdateRecommendedRouteTechMode = this._errorMessageUpdateRecommendedRouteTechMode.asReadonly();
  
  public updateRecommendedRouteTechModeFromPlannerServiceSignal(payload: UpdateRecommededRouteTechModePayload): void {
    this._updateRecommendedRouteTechMode(payload).subscribe({
      next: (_response) => {
        const message = 'La ruta ha sido guardada';
        this._successMessageUpdateRecommendedRouteTechMode.set(message);
        // HRS-2990 (fase 5): la ruta ya existe, así que aquí es donde el tipo de
        // viajero y los intereses del formulario pueden quedar como estadística
        // asociados a su token.
        this._informerRouteRegister.sendPendingRegister(payload.token, payload.route_name);
        this._sessionStorageService.removeAll();
        this._techItemsRoute.clear();
        this._router.navigateByUrl(`/home`);
      },
      error: (_error: HttpErrorResponse) => {
        const message = 'Ha ocurrido un error al guardar la ruta';
        this._errorMessageUpdateRecommendedRouteTechMode.set(message);
      }
    });
  }
  
  public updateRecommendedRouteTechModeFromRegisterUserServiceSignal(payload: UpdateRecommededRouteTechModePayload): void {
    this._updateRecommendedRouteTechMode(payload).subscribe({
      next: (_response) => {
        const message = 'Ruta actualizada correctamente';
        this._successMessageUpdateRecommendedRouteTechMode.set(message);
        this._sessionStorageService.removeAll();
        // HRS-2990: la ruta ya está guardada, así que los contenidos que se
        // fueron añadiendo dejan de tener dueño; si no, la siguiente ruta
        // empezaría con los de la anterior.
        this._techItemsRoute.clear();
        this._router.navigateByUrl(`/custom-route/v3/${payload.token}`);
      },
      error: (_error: HttpErrorResponse) => {
        const message = 'Ha ocurrido un error al actualizar la ruta';
        this._errorMessageUpdateRecommendedRouteTechMode.set(message);
      }
    });
  }
  
  private _updateRecommendedRouteTechMode(payload: UpdateRecommededRouteTechModePayload): Observable<HttpResponse<void>> {
    this._isLoadingUpdateRecommendedRouteTechMode.set(true);
    
    const url = `${environment.apiAssistantUrlBase}/api/recommended-route`;
    const formData = new FormData();
    if (payload.id) formData.append('id', String(payload.id));
    formData.append('token', payload.token);
    if (payload.start_location) formData.append('start_location', JSON.stringify(payload.start_location));
    if (payload.start_date) formData.append('start_date', payload.start_date);
    if (payload.end_date) formData.append('end_date', payload.end_date);
    if (payload.start_time) formData.append('start_time', payload.start_time);
    if (payload.route_name) formData.append('route_name', JSON.stringify(payload.route_name));
    if (payload.route_image) formData.append('route_image', payload.route_image, payload.route_image.name);
    formData.append('is_active', String(payload.is_active));
    if (payload.private !== undefined) formData.append('private', String(payload.private));

    return this._http.patch<void>(url, formData, { observe: 'response' }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
          console.error('Error updating recommended route tech mode:', error);
          return throwError(() => new Error('HTTP request failed', { cause: error }));
      }),
      finalize(() => {
        this._isLoadingUpdateRecommendedRouteTechMode.set(false);
      }
    ))
  }
}
